import React, { useEffect } from 'react'
import CalendarDayInfoHeader from './CalendarDayInfoHeader'
import { DividerHorizontal } from '@/modules/drivers/shared/DividerHorizontal'
import CalendarDayInfoActivityList from './CalendarDayInfoActivityList'
import useDriverActivityService from '@/modules/drivers/services/DriverActivityService'
import { ActivityInfo } from '@/modules/drivers/types/activities'
import CalendarDayInfoActions from './CalendarDayInfoActions'
import CalendarDayInfoAttestations from './CalendarDayInfoAttestations'
import CalendarDayInfoChart from './CalendarDayInfoChart'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { addDays } from 'date-fns'
import NoteModal from '../note/NoteModal'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { LoaderIcon } from '@/app/features/icons/Loader'

const CalendarDayInfoPopup = ({
  date,
  companyId,
  driverId,
  selectedDay,
}) => {
  const { t } = useLocalize()
  const [isOpenNoteModal, setIsOpenNoteModal] = React.useState(false)
  const [activities, setActivities] = React.useState<ActivityInfo | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const { getDriverActivity } = useDriverActivityService()
  const [refreshKey, setRefreshKey] = React.useState(0)
  const { formatApiDate } = useDate()
  const noteExists = !!activities?.notes[0]?.text
  const dateString = date.format('YYYY-MM-DD')
  useEffect(() => {
    const fetchData = async () => {
      setActivities(null)
      setIsLoading(true)
      try {
        const data = await getDriverActivity({
          companyId,
          driverId,
          startTime: formatApiDate(dateString),
          endTime: formatApiDate(addDays(dateString, 1)),
          includeTrips: true,
        })
        if (data) {
          setActivities(data)
        }
      } catch (err) {
        console.error('Error fetching driver activity:', err)
      } finally {
        setIsLoading(false)
      }
    }
    if (selectedDay && date && companyId && driverId) {
      fetchData()
    }
  }, [selectedDay, refreshKey])
  return (
    <div className="tw-absolute tw-h-fit tw-max-h-[600px] tw-w-[450px] tw-max-w-[450px] tw-rounded-lg tw-bg-white tw-pb-4 tw-shadow-lg">
      <CalendarDayInfoHeader
        isNoteExisting={noteExists}
        date={date}
        setIsOpenNoteModal={setIsOpenNoteModal}
      />
      <CalendarDayInfoChart
        isLoading={isLoading}
        currentDate={date.format('YYYY-MM-DD')}
        activities={activities as ActivityInfo}
        startTime={date.startOf('day').format()}
      />
      <div className="tw-space-y-1 tw-px-2.5">
        <DividerHorizontal />
        <CalendarDayInfoActions formattedDate={date.format('YYYY-MM-DD')} />
        <DividerHorizontal />
        {(selectedDay?.hasAttestation ||
          (selectedDay?.attestationType ?? 0) > 0) && (
          <>
            <CalendarDayInfoAttestations
              companyId={companyId}
              driverId={driverId}
              date={dateString}
              attestationType={selectedDay?.attestationType}
            />
            <DividerHorizontal />
          </>
        )}
        <p className="tw-text-xs tw-font-semibold tw-uppercase tw-text-content-base/80">
          {t('dailyActivities.activities')}
        </p>
        {isLoading && !activities ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-4">
            <LoaderIcon className="tw-h-5 tw-w-5 tw-animate-spin tw-text-primary" />
          </div>
        ) : (
          <CalendarDayInfoActivityList activities={activities} />
        )}
      </div>
      {isOpenNoteModal && (
        <NoteModal
          refreshActivities={() => setRefreshKey((prev) => prev + 1)}
          close={() => setIsOpenNoteModal(false)}
          driverId={driverId}
          companyId={companyId}
          date={date}
          existingNote={activities?.notes[0]?.text || ''}
          dayType={selectedDay?.dayType}
        />
      )}
    </div>
  )
}

export default CalendarDayInfoPopup
